import { Controller } from '@hotwired/stimulus';

export const create = () => {
	return class extends Controller {
		static targets = ['trigger', 'panel', 'badge', 'label', 'chevron'];
		static values = {
			searchable: { type: Boolean, default: true },
			searchLabel: String,
			surface: { type: String, default: 'dark' },
			shiftPanel: { type: Boolean, default: false },
		};
		static nextPanelId = 0;

		connect() {
			this._handleOutsideClick = this._handleOutsideClick.bind(this);
			this._handleFormReset = this._handleFormReset.bind(this);
			this._handleSearchInput = this._handleSearchInput.bind(this);
			this.form = this.element.closest('form');
			document.addEventListener('pointerdown', this._handleOutsideClick);
			this.form?.addEventListener('reset', this._handleFormReset);
			this._setupAccessibility();
			this._setupSearch();
			this._normalizeVisualStyle();
			this._updateBadgeAndLabel();
		}

		disconnect() {
			document.removeEventListener('pointerdown', this._handleOutsideClick);
			this.form?.removeEventListener('reset', this._handleFormReset);
			this.searchInput?.removeEventListener('input', this._handleSearchInput);
		}

		// Dropdown open/close
		toggle(e) {
			e.preventDefault();
			const isOpen = this.element.classList.contains('is-open');

			document.querySelectorAll('[data-controller~="filter-dropdown"].is-open').forEach((el) => {
				if (el !== this.element) {
					this._closeElement(el);
				}
			});

			if (isOpen) {
				this._closeElement(this.element);
			} else {
				this._openElement(this.element);
				if (this.hasPanelTarget) {
					const firstFocusable = this.panelTarget.querySelector('input, button');
					if (firstFocusable) firstFocusable.focus();
				}
			}
		}

		close() {
			this._closeElement(this.element);
			if (this.hasTriggerTarget) this.triggerTarget.focus();
		}

		_openElement(el) {
			el.classList.add('is-open');
			const trigger = el.querySelector('[data-filter-dropdown-target="trigger"]');
			const panel = el.querySelector('[data-filter-dropdown-target="panel"]');
			const chevron = el.querySelector('[data-filter-dropdown-target="chevron"]');
			if (trigger) trigger.setAttribute('aria-expanded', 'true');
			if (panel) panel.classList.remove('hidden');
			if (chevron) chevron.classList.add('rotate-180');
		}

		_closeElement(el) {
			el.classList.remove('is-open');
			const trigger = el.querySelector('[data-filter-dropdown-target="trigger"]');
			const panel = el.querySelector('[data-filter-dropdown-target="panel"]');
			const chevron = el.querySelector('[data-filter-dropdown-target="chevron"]');
			if (trigger) trigger.setAttribute('aria-expanded', 'false');
			if (panel) panel.classList.add('hidden');
			if (chevron) chevron.classList.remove('rotate-180');
		}

		_setupSearch() {
			if (!this.searchableValue) return;
			if (!this.hasPanelTarget || this.panelTarget.querySelector('[data-filter-dropdown-search]')) return;

			const optionLabels = Array.from(this.panelTarget.querySelectorAll('[data-option-label]'));
			if (optionLabels.length < 2) return;

			const searchId = `${this.panelTarget.id || 'filter-dropdown-panel'}-search`;
			const searchWrap = document.createElement('div');
			searchWrap.className = 'filter-dropdown__search';
			searchWrap.innerHTML = `
				<label class="sr-only" for="${searchId}">${this._getSearchLabel()}</label>
				<input id="${searchId}"
					type="search"
					class="filter-dropdown__search-input"
					placeholder="${this._getSearchLabel()}"
					autocomplete="off"
					data-filter-dropdown-search />
				<p class="sr-only" aria-live="polite" data-filter-dropdown-search-status></p>
			`;

			this.panelTarget.prepend(searchWrap);
			this.searchInput = searchWrap.querySelector('[data-filter-dropdown-search]');
			this.searchStatus = searchWrap.querySelector('[data-filter-dropdown-search-status]');
			this.searchInput?.addEventListener('input', this._handleSearchInput);
		}

		_handleSearchInput(e) {
			this._filterOptions(e.currentTarget.value);
		}

		_filterOptions(value) {
			if (!this.hasPanelTarget) return;

			const query = this._normalize(value);
			let visibleCount = 0;

			this.panelTarget.querySelectorAll('[data-option-label]').forEach((label) => {
				const optionRow = label.closest('label, [role="option"], li');
				if (!optionRow) return;

				const matches = !query || this._normalize(label.textContent).includes(query);
				optionRow.hidden = !matches;
				if (matches) visibleCount += 1;
			});

			if (this.searchStatus) {
				this.searchStatus.textContent = this._getResultsLabel(visibleCount);
			}
		}

		_getSearchLabel() {
			if (this.searchLabelValue) return this.searchLabelValue;
			if (document.documentElement.dataset.filterDropdownSearchLabel) {
				return document.documentElement.dataset.filterDropdownSearchLabel;
			}

			const lang = document.documentElement.lang?.toLowerCase() || '';
			return lang.startsWith('cs') ? 'Vyhledat' : 'Search';
		}

		_getResultsLabel(count) {
			const lang = document.documentElement.lang?.toLowerCase() || '';
			return lang.startsWith('cs') ? `Počet výsledků: ${count}.` : `${count} results found.`;
		}

		_normalize(value) {
			return String(value ?? '')
				.toLocaleLowerCase()
				.normalize('NFD')
				.replace(/[\u0300-\u036f]/g, '');
		}

		_handleOutsideClick(e) {
			if (!this.element.contains(e.target)) {
				this._closeElement(this.element);
			}
		}

		_normalizeVisualStyle() {
			if (this.hasTriggerTarget) {
				const triggerButton = this.triggerTarget.closest('button');
				const triggerSpan = triggerButton ? Array.from(triggerButton.children).find((child) => child.tagName === 'SPAN') : null;

				if (triggerButton) {
					triggerButton.classList.remove('rounded-full', 'cursor-pointer');
				}

				if (triggerSpan) {
					const isLightSurface = (this.surfaceValue || 'dark').toLowerCase() === 'light';
					triggerSpan.classList.remove(
						'text-sm',
						'sm:text-base',
						'lg:text-xl',
						'bg-white',
						'rounded-full',
						'border-b-3',
						'px-3',
						'sm:px-6',
						'py-2',
						'lg:py-3.5',
						'border-blue-light',
						'text-blue-light',
						'text-blue-dark',
						'font-bold',
						'transition',
						'hover:bg-blue-light',
						'hover:text-white',
						'group-[.is-open]/dropdown:!bg-blue-light',
						'group-[.is-open]/dropdown:!text-white',
						'group-[.is-open]/dropdown:!border-blue-light'
					);
					if (isLightSurface) {
						triggerSpan.classList.add(
							'inline-flex',
							'items-center',
							'justify-between',
							'w-full',
							'gap-4',
							'rounded-full',
							'border',
							'border-blue-light',
							'bg-white',
							'px-5',
							'py-3.5',
							'text-sm',
							'font-semibold',
							'text-blue-dark',
							'cursor-pointer',
							'hover:!bg-white',
							'hover:!text-blue-dark',
							'group-[.is-open]/dropdown:!bg-white',
							'group-[.is-open]/dropdown:!text-blue-dark',
							'group-[.is-open]/dropdown:!border-blue-light'
						);
					} else {
						triggerSpan.classList.add(
							'inline-flex',
							'items-center',
							'justify-between',
							'w-full',
							'gap-4',
							'rounded-full',
							'border',
							'border-white/30',
							'bg-white/6',
							'px-5',
							'py-3.5',
							'text-sm',
							'font-semibold',
							'text-white',
							'cursor-pointer',
							'hover:border-white/50',
							'hover:bg-white/10',
							'group-[.is-open]/dropdown:!bg-white',
							'group-[.is-open]/dropdown:!text-blue-dark',
							'group-[.is-open]/dropdown:!border-blue-light'
						);
					}
				}
			}

			if (this.hasChevronTarget) {
				const isLightSurface = (this.surfaceValue || 'dark').toLowerCase() === 'light';
				this.chevronTarget.classList.remove(
					'rotate-180',
					'text-white',
					'text-blue-light',
					'text-blue-dark',
					'group-[.is-open]/dropdown:!text-white',
					'group-[.is-open]/dropdown:!text-blue-dark'
				);
				if (isLightSurface) {
					this.chevronTarget.classList.add('text-blue-light', 'transition', 'group-hover/filter:text-blue-dark', 'group-[.is-open]/dropdown:!text-blue-dark');
				} else {
					this.chevronTarget.classList.add('text-white', 'transition', 'group-hover/filter:text-white', 'group-[.is-open]/dropdown:!text-blue-dark');
				}
			}

			if (this.hasPanelTarget) {
				// Always render the panel as an overlay so opening a dropdown never shifts page content.
				this.panelTarget.classList.remove('relative', 'mt-2');
				this.panelTarget.classList.add('absolute', 'top-full', 'left-0');
			}
		}

		handleKeydown(e) {
			if (e.key === 'Escape') {
				e.preventDefault();
				e.stopPropagation();
				this.close();
				return;
			}

			if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
				e.preventDefault();
				this._focusOption(e.key === 'ArrowDown' ? 1 : -1);
				return;
			}

			if (e.key === 'Home' || e.key === 'End') {
				e.preventDefault();
				this._focusOption(e.key === 'Home' ? 'first' : 'last');
			}
		}

		toggleSubPanel(e) {
			e.preventDefault();
			const btn = e.currentTarget;
			const group = btn.closest('[data-filter-group]');
			if (!group) return;
			const subPanel = group.querySelector('[data-sub-panel]');
			if (!subPanel) return;

			const isExpanded = group.classList.contains('is-expanded');
			group.classList.toggle('is-expanded', !isExpanded);
			btn.setAttribute('aria-expanded', String(!isExpanded));
			subPanel.hidden = isExpanded;
		}

		parentCheckboxChanged(e) {
			const checkbox = e.currentTarget;
			const group = checkbox.closest('[data-filter-group]');
			if (!group) return;

			group.querySelectorAll('[data-child-checkbox]').forEach((child) => {
				child.checked = checkbox.checked;
				child.indeterminate = false;
			});

			this._updateBadgeAndLabel();
		}

		childCheckboxChanged(e) {
			const checkbox = e.currentTarget;
			const group = checkbox.closest('[data-filter-group]');
			if (!group) return;

			const parentCb = group.querySelector('[data-parent-checkbox]');
			if (parentCb) {
				const all = group.querySelectorAll('[data-child-checkbox]');
				const checked = group.querySelectorAll('[data-child-checkbox]:checked');

				if (checked.length === 0) {
					parentCb.checked = false;
					parentCb.indeterminate = false;
				} else if (checked.length === all.length) {
					parentCb.checked = true;
					parentCb.indeterminate = false;
				} else {
					parentCb.checked = false;
					parentCb.indeterminate = true;
				}
			}

			this._updateBadgeAndLabel();
		}

		simpleCheckboxChanged() {
			this._updateBadgeAndLabel();
		}

		// Single-select (radio) option chosen: reflect its label on the trigger and close.
		// The form's data-ajax-autosubmit handles the actual submit on the same change event.
		selectSingle(e) {
			const input = e.currentTarget;
			const text = input.closest('label')?.querySelector('[data-option-label]')?.textContent?.trim();
			if (text && this.hasLabelTarget) {
				this.labelTarget.textContent = text;
			}
			this.close();
		}

		_handleFormReset() {
			requestAnimationFrame(() => {
				this.element.querySelectorAll('[data-filter-group]').forEach((group) => {
					const parentCb = group.querySelector('[data-parent-checkbox]');
					if (!(parentCb instanceof HTMLInputElement)) return;

					const all = group.querySelectorAll('[data-child-checkbox]');
					const checked = group.querySelectorAll('[data-child-checkbox]:checked');

					if (checked.length === 0) {
						parentCb.checked = false;
						parentCb.indeterminate = false;
					} else if (checked.length === all.length) {
						parentCb.checked = true;
						parentCb.indeterminate = false;
					} else {
						parentCb.checked = false;
						parentCb.indeterminate = true;
					}
				});

				this._updateBadgeAndLabel();
			});
		}

		_updateBadgeAndLabel() {
			const keys = new Set();
			this.element.querySelectorAll('input[type="checkbox"]:checked').forEach((input) => {
				const syncKey = input.dataset.syncName || input.name;
				if (!syncKey) return;
				keys.add(`${syncKey}:${input.value}`);
			});

			const count = keys.size;

			if (this.hasBadgeTarget) {
				this.badgeTarget.textContent = count;
				this.badgeTarget.hidden = count === 0;
				this.badgeTarget.setAttribute('aria-label', `Počet vybraných: ${count}`);
			}
		}

		_setupAccessibility() {
			if (!this.hasTriggerTarget || !this.hasPanelTarget) return;

			if (!this.panelTarget.id) {
				this.constructor.nextPanelId += 1;
				this.panelTarget.id = `filter-dropdown-panel-${this.constructor.nextPanelId}`;
			}

			this.triggerTarget.setAttribute('role', 'combobox');
			this.triggerTarget.setAttribute('aria-haspopup', 'listbox');
			this.triggerTarget.setAttribute('aria-controls', this.panelTarget.id);
			this.triggerTarget.setAttribute('aria-expanded', this.element.classList.contains('is-open') ? 'true' : 'false');

			if (!this.panelTarget.hasAttribute('role')) {
				this.panelTarget.setAttribute('role', 'listbox');
			}

			if (
				this.panelTarget.getAttribute('role') === 'listbox' &&
				this.panelTarget.querySelector('input[type="checkbox"]') &&
				!this.panelTarget.hasAttribute('aria-multiselectable')
			) {
				this.panelTarget.setAttribute('aria-multiselectable', 'true');
			}
		}

		_getOptionControls() {
			if (!this.hasPanelTarget) return [];

			return Array.from(this.panelTarget.querySelectorAll('input[type="checkbox"], input[type="radio"], button')).filter(
				(control) => !control.disabled && !control.closest('[hidden]'),
			);
		}

		_focusOption(direction) {
			const controls = this._getOptionControls();
			if (!controls.length) return;

			if (!this.element.classList.contains('is-open')) {
				this._openElement(this.element);
				(direction === -1 || direction === 'last' ? controls[controls.length - 1] : controls[0]).focus();
				return;
			}

			const currentIndex = controls.indexOf(document.activeElement);

			if (direction === 'first') {
				controls[0].focus();
				return;
			}

			if (direction === 'last') {
				controls[controls.length - 1].focus();
				return;
			}

			const nextIndex = currentIndex === -1 ? (direction > 0 ? 0 : controls.length - 1) : (currentIndex + direction + controls.length) % controls.length;
			controls[nextIndex].focus();
		}
	};
};
